import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  TextField,
  Paper,
  Divider,
  Alert,
  Chip,
  InputAdornment,
  CircularProgress,
  Collapse,
  Select,
  MenuItem,
  FormControl,
  FormHelperText
} from '@mui/material';
import {
  PersonOutline,
  Lock,
  AppRegistration,
  Verified,
  ErrorOutline,
  ReceiptLong,
  LocalOffer,
  LocationOn,
  Payment
} from '@mui/icons-material';
import { useCart } from '../../../context/CartContext';
import { useUser } from '../../../context/UserContext';
import { useNavigate } from 'react-router-dom';
import { apiList, invokeApi, invokeGetApi } from '../../../services/apiServices';
import { getProductImage } from '../../../utils/imageHelper';

// Valid Indian GSTIN regex
const GST_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

// Dynamic Cashfree SDK loader
const loadCashfree = () => {
  return new Promise((resolve, reject) => {
    if (window.Cashfree) {
      return resolve(window.Cashfree);
    }
    const script = document.createElement('script');
    script.src = 'https://sdk.cashfree.com/js/v3/cashfree.js';
    script.async = true;
    script.onload = () => {
      if (window.Cashfree) {
        resolve(window.Cashfree);
      } else {
        reject(new Error('Cashfree SDK failed to initialize'));
      }
    };
    script.onerror = () => reject(new Error('Failed to load Cashfree script'));
    document.body.appendChild(script);
  });
};

// ── Country codes (comprehensive list with flag emoji support) ──
const COUNTRY_CODES = [
  { code: '+91', country: 'IN', name: 'India' },
  { code: '+1', country: 'US', name: 'United States' },
  { code: '+1', country: 'CA', name: 'Canada' },
  { code: '+44', country: 'GB', name: 'United Kingdom' },
  { code: '+61', country: 'AU', name: 'Australia' },
  { code: '+64', country: 'NZ', name: 'New Zealand' },
  { code: '+65', country: 'SG', name: 'Singapore' },
  { code: '+971', country: 'AE', name: 'UAE' },
  { code: '+966', country: 'SA', name: 'Saudi Arabia' },
  { code: '+974', country: 'QA', name: 'Qatar' },
  { code: '+973', country: 'BH', name: 'Bahrain' },
  { code: '+968', country: 'OM', name: 'Oman' },
  { code: '+965', country: 'KW', name: 'Kuwait' },
  { code: '+60', country: 'MY', name: 'Malaysia' },
  { code: '+62', country: 'ID', name: 'Indonesia' },
  { code: '+63', country: 'PH', name: 'Philippines' },
  { code: '+66', country: 'TH', name: 'Thailand' },
  { code: '+84', country: 'VN', name: 'Vietnam' },
  { code: '+880', country: 'BD', name: 'Bangladesh' },
  { code: '+94', country: 'LK', name: 'Sri Lanka' },
  { code: '+92', country: 'PK', name: 'Pakistan' },
  { code: '+977', country: 'NP', name: 'Nepal' },
  { code: '+960', country: 'MV', name: 'Maldives' },
  { code: '+975', country: 'BT', name: 'Bhutan' },
  { code: '+81', country: 'JP', name: 'Japan' },
  { code: '+82', country: 'KR', name: 'South Korea' },
  { code: '+86', country: 'CN', name: 'China' },
  { code: '+852', country: 'HK', name: 'Hong Kong' },
  { code: '+853', country: 'MO', name: 'Macau' },
  { code: '+886', country: 'TW', name: 'Taiwan' },
  { code: '+49', country: 'DE', name: 'Germany' },
  { code: '+33', country: 'FR', name: 'France' },
  { code: '+39', country: 'IT', name: 'Italy' },
  { code: '+34', country: 'ES', name: 'Spain' },
  { code: '+31', country: 'NL', name: 'Netherlands' },
  { code: '+32', country: 'BE', name: 'Belgium' },
  { code: '+41', country: 'CH', name: 'Switzerland' },
  { code: '+43', country: 'AT', name: 'Austria' },
  { code: '+46', country: 'SE', name: 'Sweden' },
  { code: '+47', country: 'NO', name: 'Norway' },
  { code: '+45', country: 'DK', name: 'Denmark' },
  { code: '+358', country: 'FI', name: 'Finland' },
  { code: '+353', country: 'IE', name: 'Ireland' },
  { code: '+351', country: 'PT', name: 'Portugal' },
  { code: '+30', country: 'GR', name: 'Greece' },
  { code: '+48', country: 'PL', name: 'Poland' },
  { code: '+7', country: 'RU', name: 'Russia' },
  { code: '+380', country: 'UA', name: 'Ukraine' },
  { code: '+27', country: 'ZA', name: 'South Africa' },
  { code: '+234', country: 'NG', name: 'Nigeria' },
  { code: '+20', country: 'EG', name: 'Egypt' },
  { code: '+254', country: 'KE', name: 'Kenya' },
  { code: '+233', country: 'GH', name: 'Ghana' },
  { code: '+55', country: 'BR', name: 'Brazil' },
  { code: '+52', country: 'MX', name: 'Mexico' },
  { code: '+54', country: 'AR', name: 'Argentina' },
  { code: '+56', country: 'CL', name: 'Chile' },
  { code: '+57', country: 'CO', name: 'Colombia' },
  { code: '+58', country: 'VE', name: 'Venezuela' },
  { code: '+51', country: 'PE', name: 'Peru' },
];

const FLAG_EMOJI = (countryCode) => {
  const offset = 127397;
  return [...countryCode.toUpperCase()].map(c => String.fromCodePoint(c.charCodeAt(0) + offset)).join('');
};

// ── Coupon codes ──
const COUPONS = {
  'ARINFOTECH10': { type: 'percent', value: 10, label: '10% Off' },
  'WELCOME50': { type: 'flat', value: 50, label: '₹50 Off' },
  'BULK200': { type: 'flat', value: 200, label: '₹200 Off' },
};

const CheckoutPage = () => {
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [selectedCountryIdx, setSelectedCountryIdx] = useState(0);
  const [addressForm, setAddressForm] = useState({
    fullName: '',
    phoneNumber: '',
    email: '',
    companyName: '',
    gstNumber: '',
    addressLine1: '',
    addressLine2: '',
    pincode: '',
    city: '',
    state: '',
    country: ''
  });

  const [errors, setErrors] = useState({});
  const [gstStatus, setGstStatus] = useState(null); // null | 'valid' | 'invalid'
  const [pincodeLoading, setPincodeLoading] = useState(false);

  // Coupon state
  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [couponSuccess, setCouponSuccess] = useState('');

  const { cartItems, subtotal, clearCart } = useCart();
  const { user } = useUser();
  const navigate = useNavigate();

  // const shipping = subtotal > 999 ? 0 : 1;
  const gstAmount = subtotal * 0.18;

  // Discount calculation
  const discountAmount = appliedCoupon
    ? appliedCoupon.type === 'percent'
      ? (subtotal * appliedCoupon.value) / 100
      : Math.min(appliedCoupon.value, subtotal)
    : 0;

  const estimatedTotal = subtotal + gstAmount - discountAmount;

  // ── Pincode auto-fill ──
  const handlePincodeChange = async (e) => {
    const pin = e.target.value;
    setAddressForm(prev => ({ ...prev, pincode: pin, city: '', state: '', country: '' }));

    if (pin.length === 6 && /^\d+$/.test(pin)) {
      setPincodeLoading(true);
      try {
        const response = await fetch(`https://api.postalpincode.in/pincode/${pin}`);
        const data = await response.json();
        if (data && data[0].Status === 'Success') {
          const postOffice = data[0].PostOffice[0];
          setAddressForm(prev => ({
            ...prev,
            city: postOffice.District,
            state: postOffice.State,
            country: 'India'
          }));
        }
      } catch (err) {
        console.error('Pincode lookup failed:', err);
      } finally {
        setPincodeLoading(false);
      }
    }
  };

  const handleAddressChange = (field) => (e) => {
    setAddressForm(prev => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  // ── GST validation on blur ──
  const handleGstBlur = () => {
    const gst = addressForm.gstNumber.trim().toUpperCase();
    if (!gst) { setGstStatus(null); return; }
    if (GST_REGEX.test(gst)) {
      setGstStatus('valid');
      setAddressForm(prev => ({ ...prev, gstNumber: gst }));
    } else {
      setGstStatus('invalid');
    }
  };

  // ── Form validation ──
  const validateForm = () => {
    const required = {
      fullName: 'Full name is required',
      phoneNumber: 'Phone number is required',
      email: 'Email address is required',
      addressLine1: 'Address is required',
      pincode: 'Pincode is required',
      city: 'City is required',
      state: 'State is required',
      country: 'Country is required',
    };

    const newErrors = {};

    Object.entries(required).forEach(([field, msg]) => {
      if (!addressForm[field]?.trim()) newErrors[field] = msg;
    });

    // Email format
    if (addressForm.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addressForm.email)) {
      newErrors.email = 'Enter a valid email address';
    }

    // Phone: digits only, 6–15 digits
    if (addressForm.phoneNumber && !/^\d{6,15}$/.test(addressForm.phoneNumber.replace(/\s/g, ''))) {
      newErrors.phoneNumber = 'Enter a valid phone number (digits only)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ── Coupon apply ──
  const handleApplyCoupon = () => {
    setCouponError('');
    setCouponSuccess('');
    const code = couponInput.trim().toUpperCase();
    if (!code) { setCouponError('Please enter a coupon code.'); return; }
    const coupon = COUPONS[code];
    if (!coupon) {
      setCouponError('Invalid coupon code. Please check and try again.');
      return;
    }
    setAppliedCoupon({ ...coupon, code });
    setCouponSuccess(`Coupon "${code}" applied! You save ${coupon.type === 'percent' ? `${coupon.value}%` : `₹${coupon.value}`}.`);
    setCouponInput('');
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponError('');
    setCouponSuccess('');
  };

  // ── Cashfree direct payment ──
  const handleCashfreePayment = async () => {
    if (!validateForm()) return;

    setIsProcessingPayment(true);
    setPaymentError('');
    try {
      const CashfreeSDK = await loadCashfree();

      let mode = 'sandbox';
      try {
        const configRes = await invokeGetApi(apiList.getCashfreeConfig);
        if (configRes?.data?.mode) {
          mode = configRes.data.mode;
        }
      } catch (e) {
        console.log('Using default mode:', mode);
      }

      const cashfree = CashfreeSDK({ mode });

      const fullAddress = [
        addressForm.addressLine1,
        addressForm.addressLine2,
        addressForm.city,
        addressForm.state,
        addressForm.pincode,
        addressForm.country
      ].filter(Boolean).join(', ');

      const orderPayload = {
        totalAmount: estimatedTotal,
        fullName: addressForm.fullName,
        email: addressForm.email,
        phoneNumber: `${countryCode}${addressForm.phoneNumber}`,
        address: fullAddress,
        items: cartItems.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity,
          size: item.size
        })),
        returnUrl: window.location.origin + '/order-success'
      };

      const orderRes = await invokeApi(apiList.createCashfreeOrder, orderPayload);

      if (orderRes?.data?.payment_session_id) {
        clearCart();
        cashfree.checkout({
          paymentSessionId: orderRes.data.payment_session_id,
          redirectTarget: '_self'
        });
      } else {
        throw new Error(orderRes?.data?.responseMessage || 'Could not initiate Cashfree payment session');
      }
    } catch (err) {
      console.error('Cashfree Checkout Error:', err);
      setPaymentError(err.message || 'Payment initiation failed. Please try again.');
      setIsProcessingPayment(false);
    }
  };

  // Show review section as soon as user starts filling
  const isAddressPartiallyFilled = !!(addressForm.fullName || addressForm.addressLine1 || addressForm.city);

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <Typography variant="h3" align="center" gutterBottom mb={6} sx={{ fontWeight: 800 }}>
        Checkout
      </Typography>

      <Grid container spacing={4}>
        {/* ── Left Column ── */}
        <Grid item xs={12} md={8}>

          {/* ── Guest Sign-In Banner ── */}
          {!user && (
            <Paper
              elevation={0}
              sx={{
                mb: 3,
                borderRadius: 3,
                overflow: 'hidden',
                border: '1.5px solid #e3edff',
                background: 'linear-gradient(135deg, #f0f6ff 0%, #ffffff 60%, #f5f0ff 100%)'
              }}
            >
              <Box sx={{ px: 3, pt: 3, pb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 800, color: '#1a237e', mb: 0.5 }}>
                  Have an account? Get faster checkout!
                </Typography>
                <Typography variant="body2" sx={{ color: '#555', mb: 2.5 }}>
                  Sign in to use your saved addresses, track orders, and earn rewards. Or continue below as a guest.
                </Typography>
              </Box>
              <Divider />
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, px: 3, py: 2.5 }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<PersonOutline />}
                  onClick={() => navigate('/login')}
                  sx={{
                    flex: 1, py: 1.5, borderRadius: 2.5, fontWeight: 700, fontSize: '1rem',
                    background: 'linear-gradient(135deg, #1565c0, #0e6ada)',
                    boxShadow: '0 4px 15px rgba(14,106,218,0.35)',
                    textTransform: 'none',
                    '&:hover': { background: 'linear-gradient(135deg, #0d47a1, #0958b8)' }
                  }}
                >
                  Sign In
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<AppRegistration />}
                  onClick={() => navigate('/login')}
                  sx={{
                    flex: 1, py: 1.5, borderRadius: 2.5, fontWeight: 700, fontSize: '1rem',
                    borderColor: '#1565c0', color: '#1565c0', borderWidth: 1.5, textTransform: 'none',
                    '&:hover': { borderColor: '#0d47a1', bgcolor: '#f0f6ff', borderWidth: 1.5 }
                  }}
                >
                  Create Account
                </Button>
              </Box>
              <Box sx={{ px: 3, pb: 2 }}>
                <Typography variant="caption" sx={{ color: '#888', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Lock sx={{ fontSize: 13 }} /> Your data is safe & encrypted. You can also fill the form below as a guest.
                </Typography>
              </Box>
            </Paper>
          )}

          {/* ── Main Form Paper ── */}
          <Paper sx={{ p: 4, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>
              {user ? 'Shipping Address' : 'Guest Shipping Address'}
            </Typography>

            <Grid container spacing={2.5}>

              <Grid item xs={12}>
                <TextField
                  fullWidth label="Full Name *" variant="outlined"
                  value={addressForm.fullName} onChange={handleAddressChange('fullName')}
                  error={!!errors.fullName} helperText={errors.fullName}
                />
              </Grid>

              {/* ── Phone Number with Country Code Dropdown ── */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <FormControl sx={{ minWidth: 120 }} error={!!errors.phoneNumber}>
                    <Select
                      value={selectedCountryIdx}
                      onChange={(e) => {
                        const idx = e.target.value;
                        setSelectedCountryIdx(idx);
                        setCountryCode(COUNTRY_CODES[idx].code);
                      }}
                      variant="outlined"
                      sx={{
                        height: '56px',
                        borderRadius: 1,
                        fontSize: '0.875rem',
                        '.MuiSelect-select': { py: '14px', px: '10px' }
                      }}
                      MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                      renderValue={(idx) => (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <span style={{ fontSize: '1.1rem' }}>{FLAG_EMOJI(COUNTRY_CODES[idx].country)}</span>
                          <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{COUNTRY_CODES[idx].code}</span>
                        </Box>
                      )}
                    >
                      {COUNTRY_CODES.map((c, idx) => (
                        <MenuItem key={`${c.country}-${idx}`} value={idx} sx={{ fontSize: '0.875rem', gap: 1 }}>
                          <span style={{ fontSize: '1.1rem', marginRight: 6 }}>{FLAG_EMOJI(c.country)}</span>
                          <span style={{ fontWeight: 600, marginRight: 6, minWidth: 42 }}>{c.code}</span>
                          <span style={{ color: '#555' }}>{c.name}</span>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    fullWidth
                    label="Phone Number *"
                    variant="outlined"
                    value={addressForm.phoneNumber}
                    onChange={handleAddressChange('phoneNumber')}
                    error={!!errors.phoneNumber}
                    helperText={errors.phoneNumber || 'e.g. 9876543210'}
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label="Email Address *" variant="outlined" type="email"
                  value={addressForm.email} onChange={handleAddressChange('email')}
                  error={!!errors.email} helperText={errors.email}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth label="Company Name (Optional)" variant="outlined"
                  value={addressForm.companyName} onChange={handleAddressChange('companyName')}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="GST Number (Optional)"
                  variant="outlined"
                  value={addressForm.gstNumber}
                  onChange={handleAddressChange('gstNumber')}
                  onBlur={handleGstBlur}
                  placeholder="e.g. 27ABCDE1234F1Z5"
                  InputProps={{
                    endAdornment: gstStatus && (
                      <InputAdornment position="end">
                        {gstStatus === 'valid'
                          ? <Verified sx={{ color: 'success.main' }} />
                          : <ErrorOutline sx={{ color: 'error.main' }} />
                        }
                      </InputAdornment>
                    )
                  }}
                  error={gstStatus === 'invalid'}
                  helperText={
                    gstStatus === 'valid'
                      ? '✓ Valid GSTIN — GST invoice will be generated'
                      : gstStatus === 'invalid'
                        ? 'Invalid GSTIN format. Example: 27ABCDE1234F1Z5'
                        : 'Enter your 15-digit GSTIN to receive a GST invoice'
                  }
                />
              </Grid>

              <Collapse in={gstStatus === 'valid'} sx={{ width: '100%', px: 1.25, mt: -1 }}>
                <Alert severity="success" icon={<ReceiptLong fontSize="small" />} sx={{ borderRadius: 2, py: 0.5 }}>
                  <strong>B2B GST Invoice</strong>: GST @ 18% is applied on the subtotal. You can claim Input Tax Credit (ITC) using your GSTIN.
                </Alert>
              </Collapse>

              <Grid item xs={12}>
                <Divider sx={{ my: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">Delivery Address</Typography>
                </Divider>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth label="Address Line 1 — House/flat number, building, street *"
                  variant="outlined" value={addressForm.addressLine1} onChange={handleAddressChange('addressLine1')}
                  error={!!errors.addressLine1} helperText={errors.addressLine1}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth label="Address Line 2 — Apartment, area, landmark (optional)"
                  variant="outlined" value={addressForm.addressLine2} onChange={handleAddressChange('addressLine2')}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth label="Pin Code / ZIP Code *" variant="outlined"
                  value={addressForm.pincode} onChange={handlePincodeChange}
                  error={!!errors.pincode} helperText={errors.pincode}
                  InputProps={{
                    endAdornment: pincodeLoading && (
                      <InputAdornment position="end"><CircularProgress size={18} /></InputAdornment>
                    )
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth label="City *" variant="outlined"
                  value={addressForm.city} onChange={handleAddressChange('city')}
                  error={!!errors.city} helperText={errors.city}
                  InputLabelProps={{ shrink: !!addressForm.city }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth label="State *" variant="outlined"
                  value={addressForm.state} onChange={handleAddressChange('state')}
                  error={!!errors.state} helperText={errors.state}
                  InputLabelProps={{ shrink: !!addressForm.state }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth label="Country *" variant="outlined"
                  value={addressForm.country} onChange={handleAddressChange('country')}
                  error={!!errors.country} helperText={errors.country}
                  InputLabelProps={{ shrink: !!addressForm.country }}
                />
              </Grid>
            </Grid>

            {/* ── Dynamic: Review Your Order (inline, below Delivery Address) ── */}
            <Collapse in={isAddressPartiallyFilled}>
              <Box sx={{ mt: 4 }}>
                <Divider sx={{ mb: 3 }}>
                  <Chip
                    icon={<LocationOn sx={{ fontSize: 16 }} />}
                    label="Review Your Order"
                    sx={{ fontWeight: 700, fontSize: '0.82rem', px: 1 }}
                    color="primary"
                    variant="outlined"
                  />
                </Divider>

                {/* Delivery Summary Card */}
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    mb: 3,
                    bgcolor: '#F8FAFF',
                    border: '1.5px solid #dce9ff'
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.secondary', letterSpacing: 1, fontSize: '0.72rem' }}>
                    DELIVERING TO
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                    <LocationOn sx={{ color: '#0e6ada', mt: 0.2, fontSize: 20 }} />
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>
                        {addressForm.fullName || <span style={{ color: '#aaa', fontStyle: 'italic' }}>Full Name</span>}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4, lineHeight: 1.6 }}>
                        {[addressForm.addressLine1, addressForm.addressLine2, addressForm.city, addressForm.state, addressForm.pincode, addressForm.country]
                          .filter(Boolean).join(', ') || <span style={{ color: '#bbb', fontStyle: 'italic' }}>Address will appear here</span>}
                      </Typography>
                      {(addressForm.email || addressForm.phoneNumber) && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
                          {addressForm.email}
                          {addressForm.email && addressForm.phoneNumber && ' · '}
                          {addressForm.phoneNumber && `${countryCode} ${addressForm.phoneNumber}`}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Paper>

                {/* Order Items */}
                {cartItems.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, color: 'text.secondary', letterSpacing: 1, fontSize: '0.72rem' }}>
                      ORDER ITEMS
                    </Typography>
                    {cartItems.map((item) => (
                      <Box
                        key={`${item.id}-${item.size}`}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 1.5,
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: '#F8FAFC',
                          border: '1px solid #E2E8F0'
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box
                            component="img"
                            src={getProductImage(item.image || item.featuredImage)}
                            alt={item.name}
                            sx={{
                              width: 44,
                              height: 44,
                              bgcolor: '#FFF',
                              borderRadius: 1,
                              border: '1px solid #E2E8F0',
                              objectFit: 'contain',
                              p: 0.4
                            }}
                          />
                          <Box>
                            <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#0F172A' }}>{item.name}</Typography>
                            {item.size && (
                              <Typography variant="caption" color="text.secondary">License: {item.size}</Typography>
                            )}
                          </Box>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography sx={{ fontWeight: 800, color: '#0e6ada', fontSize: '0.92rem' }}>₹{(item.price * item.quantity).toLocaleString('en-IN')}</Typography>
                          <Typography variant="caption" color="text.secondary">Qty: {item.quantity}</Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}

                {gstStatus === 'valid' && (
                  <Alert severity="info" icon={<ReceiptLong fontSize="small" />} sx={{ mb: 2, borderRadius: 2 }}>
                    GST Invoice will be sent to <strong>{addressForm.email}</strong> for GSTIN: <strong>{addressForm.gstNumber}</strong>
                  </Alert>
                )}

                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  By placing this order, you agree to our{' '}
                  <a href="/terms-conditions" style={{ color: '#0e6ada' }}>terms and conditions</a>.
                </Typography>
              </Box>
            </Collapse>

            {/* ── Payment Error ── */}
            {paymentError && (
              <Alert severity="error" sx={{ mt: 3, borderRadius: 2 }}>
                {paymentError}
              </Alert>
            )}

            {/* ── Cashfree Payment Button ── */}
            <Box sx={{ mt: 4 }}>
              <Alert severity="success" sx={{ mb: 2.5, borderRadius: 2, bgcolor: '#ECFDF5', color: '#065F46' }}>
                🔒 <strong>100% Safe &amp; Secure Checkout</strong> — 256-bit encrypted bank-grade checkout powered by Cashfree Payments.
              </Alert>

              <Button
                onClick={handleCashfreePayment}
                variant="contained"
                size="large"
                fullWidth
                disabled={isProcessingPayment}
                startIcon={isProcessingPayment ? <CircularProgress size={20} color="inherit" /> : <Payment />}
                sx={{
                  py: 2,
                  borderRadius: 2.5,
                  fontWeight: 800,
                  fontSize: '1.05rem',
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, #1565c0, #0e6ada)',
                  boxShadow: '0 6px 20px rgba(14,106,218,0.4)',
                  letterSpacing: 0.5,
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0d47a1, #0958b8)',
                    boxShadow: '0 8px 25px rgba(14,106,218,0.5)',
                    transform: 'translateY(-1px)'
                  },
                  '&:active': { transform: 'translateY(0)' },
                  transition: 'all 0.2s ease'
                }}
              >
                {isProcessingPayment ? 'Initiating Payment…' : '🔐 Pay via Cashfree Payment Gateway'}
              </Button>

              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5, mt: 2, flexWrap: 'wrap' }}>
                <Typography variant="caption" color="text.secondary">Accepts:</Typography>
                {['UPI', 'Credit Card', 'Debit Card', 'Net Banking', 'Wallets'].map(method => (
                  <Chip key={method} label={method} size="small" variant="outlined" sx={{ fontSize: '0.7rem', height: 20, fontWeight: 600 }} />
                ))}
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* ── Right Column: Cart Summary ── */}
        <Grid item xs={12} md={4}>
          <Paper elevation={0} sx={{ p: 4, borderRadius: 3, backgroundColor: '#F3F7FF', border: '1px solid #dce9ff', position: 'sticky', top: 90 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 800 }}>Order Summary</Typography>

            {cartItems.map((item) => (
              <Box key={item.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: '70%' }}>
                  {item.name} × {item.quantity}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>₹{item.price * item.quantity}</Typography>
              </Box>
            ))}

            <Divider sx={{ my: 2 }} />

            {/* ── Coupon Section ── */}
            {/* <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LocalOffer sx={{ fontSize: 16 }} /> Coupon Code
              </Typography>

              {appliedCoupon ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    icon={<CheckCircle sx={{ fontSize: 16 }} />}
                    label={`${appliedCoupon.code} — ${appliedCoupon.label}`}
                    color="success"
                    onDelete={handleRemoveCoupon}
                    sx={{ fontWeight: 700, flexGrow: 1 }}
                  />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Enter coupon code"
                    value={couponInput}
                    onChange={e => { setCouponInput(e.target.value); setCouponError(''); }}
                    onKeyDown={e => e.key === 'Enter' && handleApplyCoupon()}
                    InputProps={{ sx: { borderRadius: 1.5, bgcolor: '#fff', fontSize: '0.85rem' } }}
                  />
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleApplyCoupon}
                    sx={{ borderRadius: 1.5, fontWeight: 700, textTransform: 'none', whiteSpace: 'nowrap', px: 2 }}
                  >
                    Apply
                  </Button>
                </Box>
              )}

              {couponError && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>{couponError}</Typography>
              )}
              {couponSuccess && (
                <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block', fontWeight: 600 }}>
                  ✓ {couponSuccess}
                </Typography>
              )}
              <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                Try: ARINFOTECH10 · WELCOME50 · BULK200
              </Typography>
            </Box> */}

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography color="text.secondary">Items Subtotal</Typography>
              <Typography sx={{ fontWeight: 700 }}>₹{subtotal}</Typography>
            </Box>
            {/* <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography color="text.secondary">Shipping</Typography>
              <Typography sx={{ fontWeight: 700, color: shipping === 0 ? 'success.main' : 'inherit' }}>
                {shipping === 0 ? 'FREE' : `₹${shipping}`}
              </Typography>
            </Box> */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
              <Typography color="text.secondary">GST (18%)</Typography>
              <Typography sx={{ fontWeight: 700 }}>₹{gstAmount.toFixed(2)}</Typography>
            </Box>

            {appliedCoupon && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography color="success.main" sx={{ fontWeight: 600 }}>Discount ({appliedCoupon.code})</Typography>
                <Typography sx={{ fontWeight: 700, color: 'success.main' }}>−₹{discountAmount.toFixed(2)}</Typography>
              </Box>
            )}

            {gstStatus === 'valid' && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <Chip
                  icon={<LocalOffer sx={{ fontSize: 14 }} />}
                  label="GST Invoice Eligible"
                  color="success"
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>Total (Incl. GST)</Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#0e6ada' }}>
                ₹{estimatedTotal.toFixed(2)}
              </Typography>
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              GST @ 18% included: ₹{gstAmount.toFixed(2)}
            </Typography>

            {/* {shipping > 0 && (
              <Alert severity="info" sx={{ mt: 2, borderRadius: 2, py: 0.5, fontSize: '0.75rem' }}>
                Add ₹{999 - subtotal} more to get <strong>FREE shipping</strong>!
              </Alert>
            )} */}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CheckoutPage;
